import { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { colors, s8 } from '@ardoq/design-tokens';
import { header4, text2 } from '@ardoq/typography';
import { ChevronIconName, Icon } from '@ardoq/icons';
import { VIEW_LEGEND_ROW_TOP_MARGIN } from './consts';
import { NO_EXPORT_CLASS_NAME } from '@ardoq/global-consts';
import {
  LEGEND_CONTENT_HORIZONTAL_PADDING,
  LEGEND_CONTENT_MIN_HEIGHT,
  LEGEND_CONTENT_VERTICAL_PADDING,
  LEGEND_ROW_BORDER_WIDTH,
  LEGEND_ROW_HEADER_HEIGHT,
  exportingViewLegendRowContentStyle,
} from './viewLegendConsts';

const ViewLegendCollapseIcon = styled(Icon).attrs({
  className: NO_EXPORT_CLASS_NAME,
})`
  color: ${colors.grey900};
  margin: ${s8};
`;

const ViewLegendRowElement = styled.div<{ $collapsed: boolean }>`
  border: ${LEGEND_ROW_BORDER_WIDTH}px solid ${colors.grey200};
  background-color: ${colors.white950};
  flex: 0 1 auto;
  min-height: ${({ $collapsed }) =>
    LEGEND_ROW_HEADER_HEIGHT + ($collapsed ? 0 : LEGEND_CONTENT_MIN_HEIGHT)}px;
  margin-top: ${({ $collapsed }) =>
    $collapsed ? '1px' : VIEW_LEGEND_ROW_TOP_MARGIN};
  color: ${colors.grey900};
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;

  .forExport &:first-child {
    margin-top: 0;
  }
`;
const ViewLegendRowHeading = styled.div`
  flex: 0 0 ${LEGEND_ROW_HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  text-align: center;
  background-color: ${colors.grey200};
  padding: 2px 0;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
`;
const ViewLegendRowLabel = styled.span`
  ${header4};
  line-height: 24px;
  width: 100%;
  margin-right: ${s8};
`;
export const ViewLegendRowContent = styled.div<{ $collapsed: boolean }>`
  ${({ $collapsed }) =>
    $collapsed // when the row is collapsed, we still display this with height: 0, so the width does not change when collapsing and expanding
      ? css`
          height: 0;
          padding: 0 ${LEGEND_CONTENT_HORIZONTAL_PADDING};
          overflow: hidden;
        `
      : css`
          padding: ${LEGEND_CONTENT_VERTICAL_PADDING}
            ${LEGEND_CONTENT_HORIZONTAL_PADDING};
          overflow: auto;
        `}

  flex: 0 1 auto;
  min-height: 0;
  ${text2};
  line-height: unset;

  .forExport & {
    ${exportingViewLegendRowContentStyle}
  }
`;
type ViewLegendRowArgs = {
  collapsed: boolean;
  toggleRowCollapsed: () => void;
  label: string;
  className?: string;
};

export const ViewLegendRow = ({
  collapsed,
  toggleRowCollapsed,
  label,
  className,
  children,
}: PropsWithChildren<ViewLegendRowArgs>) => (
  <ViewLegendRowElement
    data-test-id="individual-legend-panel-container"
    $collapsed={collapsed}
    className={className}
  >
    <ViewLegendRowHeading
      data-test-id="legend-collapse-toggle"
      onClick={e => {
        e.preventDefault();
        toggleRowCollapsed();
      }}
    >
      <ViewLegendCollapseIcon
        iconName={collapsed ? ChevronIconName.DOWN : ChevronIconName.UP}
      />
      <ViewLegendRowLabel>{label}</ViewLegendRowLabel>
    </ViewLegendRowHeading>
    <ViewLegendRowContent $collapsed={collapsed}>
      {children}
    </ViewLegendRowContent>
  </ViewLegendRowElement>
);
